import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

/** Pages shown on either side of the current one before the rest collapse into a gap. */
const SIBLING_PAGES = 1;

type PageSlot = { kind: 'page'; index: number } | { kind: 'gap'; key: string };

/** The page buttons to draw: the first and last page always, the current one with its neighbours. */
export function paginationSlots(page: number, numberOfPages: number): PageSlot[] {
  const indexes = new Set<number>([0, numberOfPages - 1]);
  for (let index = page - SIBLING_PAGES; index <= page + SIBLING_PAGES; index++) {
    if (index >= 0 && index < numberOfPages) {
      indexes.add(index);
    }
  }

  const sorted = Array.from(indexes).sort((left, right) => left - right);
  const slots: PageSlot[] = [];
  sorted.forEach((index, position) => {
    const previous = sorted[position - 1];
    if (previous !== undefined && index - previous > 1) {
      slots.push({ kind: 'gap', key: `gap-${previous}` });
    }
    slots.push({ kind: 'page', index });
  });
  return slots;
}

const PAGE_BUTTON_CLASSES =
  'inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-40 aria-disabled:cursor-wait aria-disabled:opacity-60';

/**
 * The footer of a paged list: where the reader is ("Showing 11–20 of 25 pages") and the controls to
 * move, as numbered buttons with previous and next beside them.
 *
 * Where the list's card is narrower than one row of it, the two halves go onto two lines and the
 * buttons onto more, so nothing runs past the card. Where the reader is is a polite live region, so a
 * page change is heard as well as seen.
 */
export function BrandPresencePagination({
  page,
  numberOfPages,
  itemsPerPage,
  totalNumberOfItems,
  itemLabel,
  onPageChange,
  shownOnPage,
  busy,
  pageSizeOptions,
  onPageSizeChange,
}: {
  page: number;
  numberOfPages: number;
  itemsPerPage: number;
  totalNumberOfItems: number;
  /** The plural noun the list counts, such as "pages". */
  itemLabel: string;
  onPageChange: (page: number) => void;
  /**
   * How many rows the page shows, where that can be fewer than a full page before the list is read
   * again: a list whose rows leave it once they are dealt with. The range then ends at the last row
   * shown rather than at the page's end.
   */
  shownOnPage?: number;
  /**
   * The list is being read again after a change on it. A page change made now would be made against
   * a count that is about to change, so the buttons take none: they keep their place and the focus,
   * and say that they are unavailable until the read has answered.
   */
  busy?: boolean;
  /** The row counts the reader may choose between; without them the footer offers no choice. */
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
}) {
  const first = totalNumberOfItems ? page * itemsPerPage + 1 : 0;
  const last = shownOnPage
    ? Math.min(totalNumberOfItems, first + shownOnPage - 1)
    : Math.min(totalNumberOfItems, (page + 1) * itemsPerPage);
  const slots = React.useMemo(() => paginationSlots(page, numberOfPages), [page, numberOfPages]);
  const pageSizeSelectId = React.useId();
  const goTo = (next: number) => {
    if (!busy) {
      onPageChange(next);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200/80 px-5 py-3.5">
      <div className="flex flex-wrap items-center gap-4">
        <p className="m-0 text-sm text-slate-500" aria-live="polite" aria-atomic="true">
          Showing{' '}
          <span className="font-semibold text-slate-700 tabular-nums">
            {first}–{last}
          </span>{' '}
          of <span className="font-semibold text-slate-700 tabular-nums">{totalNumberOfItems}</span> {itemLabel}
        </p>
        {pageSizeOptions?.length && onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <label htmlFor={pageSizeSelectId} className="text-sm text-slate-500">
              Rows per page
            </label>
            <select
              id={pageSizeSelectId}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
              value={itemsPerPage}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
      <nav className="flex flex-wrap items-center gap-1" aria-label="Pagination">
        <button
          type="button"
          className={`${PAGE_BUTTON_CLASSES} text-slate-500 hover:bg-slate-100 hover:text-slate-900`}
          disabled={page <= 0}
          aria-disabled={busy || undefined}
          onClick={() => goTo(Math.max(0, page - 1))}
          aria-label="Previous page"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
        </button>
        {slots.map((slot) =>
          slot.kind === 'gap' ? (
            <span key={slot.key} aria-hidden="true" className="px-1 text-sm text-slate-400">
              …
            </span>
          ) : (
            <button
              key={slot.index}
              type="button"
              className={`${PAGE_BUTTON_CLASSES} tabular-nums ${
                slot.index === page ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
              aria-current={slot.index === page ? 'page' : undefined}
              aria-label={`Page ${slot.index + 1}`}
              aria-disabled={busy || undefined}
              onClick={() => goTo(slot.index)}
            >
              {slot.index + 1}
            </button>
          )
        )}
        <button
          type="button"
          className={`${PAGE_BUTTON_CLASSES} text-slate-500 hover:bg-slate-100 hover:text-slate-900`}
          disabled={page >= numberOfPages - 1}
          aria-disabled={busy || undefined}
          onClick={() => goTo(Math.min(numberOfPages - 1, page + 1))}
          aria-label="Next page"
        >
          <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
        </button>
      </nav>
    </div>
  );
}
